/**
 * @file FleetTracking.js
 * @version 2.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-01-06
 * @modified 2026-10-29
 * @description Fleet tracking component with live vehicle positions and number plates.
 *              Renders both tracking providers (RAM Tracking and RIO/MAN) from the
 *              merged /api/fleet/vehicles feed, tagged by provider.
 *
 *              2.5.0 (2026-10-29, N8 — the rep phone): on the phone shell the
 *              vehicle list folds away when a vehicle is chosen from it (the
 *              map centres on it, which on a phone is under the open list),
 *              a popup is panned clear of the Vehicles tab on the right, and
 *              the first view fits the lorries with the phone's own margins.
 *              The layout itself is RepMobile.css. Desktop unchanged.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { API_URL } from '../config';
import { formatTimeLondon } from '../utils/dateFormatters';
import { formatTonnes } from '../utils/numberFormatters';
import { MapContainer, Marker, Popup, useMap } from 'react-leaflet';
import useRepMobile from '../hooks/useRepMobile';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './FleetTracking.css';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Tracking providers behind the merged /api/fleet/vehicles feed.
const PROVIDER_LABELS = {
  ram: 'RAM Tracking',
  rio: 'RIO telematics (MAN)'
};

// Status colors: moving=green, idle=blue, off=red
const STATUS_COLORS = {
  moving: '#4ade80',  // --color-success
  idle: '#1eb4ff',    // --color-primary
  off: '#f87171'      // --color-error
};

// Top-down lorry SVG with number plate (plate NOT rotated)
const createVehicleIcon = (vehicle) => {
  const status = vehicle.status || 'off';
  const color = STATUS_COLORS[status] || STATUS_COLORS.off;
  const heading = vehicle.heading || 0;
  const plate = vehicle.registration || vehicle.name || '';

  return L.divIcon({
    className: 'vehicle-marker-icon',
    html: `
      <div class="vehicle-marker-wrapper">
        <div class="vehicle-icon-svg" style="transform: rotate(${heading}deg);">
          <svg viewBox="0 0 183.43 500" width="32" height="32" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));">
            <path fill="${color}" d="M182.32,39.71c-.55-5.72-5.64-9.9-11.36-9.35l-3.6.35.51,5.23-8.07.78c-1.54-7.65-3.55-14.74-5.42-20.56-2.91-9.03-11.23-15.2-20.72-15.46C121.13.36,103.58-.04,91.72,0c-11.88-.04-29.41.36-41.87.7-9.43.26-17.72,6.36-20.67,15.33-1.92,5.84-3.98,12.98-5.54,20.69l-8.07-.78.51-5.23-3.6-.35c-5.72-.55-10.8,3.63-11.36,9.35L.05,50.65c-.55,5.72,3.63,10.8,9.35,11.36l3.6.35,1.85-19.11,7.42.72c-7.35,41.54-9.69,98.56-7.72,100.66.88.94,29.69,1.1-.72,10.13l.07,322.39c-.5,10.61,8.88,18.21,18.51,20.33,6.83,1.5,33.01,2.37,59.3,2.53,26.29-.16,52.48-1.03,59.3-2.53,9.63-2.12,19.01-9.72,18.51-20.33l.07-322.39c-30.41-9.02-1.6-9.19-.72-10.13,1.96-2.1-.37-59.12-7.72-100.66l7.42-.72,1.85,19.11,3.6-.35c5.72-.55,9.9-5.64,9.35-11.36l-1.06-10.94ZM157.69,478.89H25.75v-3.88h131.94v3.88ZM157.69,428.4H25.75v-3.88h131.94v3.88ZM157.69,377.91H25.75v-3.88h131.94v3.88ZM157.69,327.42H25.75v-3.88h131.94v3.88ZM157.69,273.04v3.88H25.75v-3.88h131.94ZM91.72,222.55h62.09v3.88H29.63v-3.88h62.09ZM29.63,175.94v-3.88h124.17v3.88H29.63ZM153.54,133.58H29.89c-2.44,0-4.42-1.98-4.42-4.42l2.57-7.28c.97-2.75,2.99-4.97,5.64-6.18,6.97-3.19,23.51-8.34,57.71-8.34.11,0,.21,0,.32,0s.21,0,.32,0c34.2,0,50.74,5.15,57.71,8.34,2.65,1.21,4.67,3.43,5.64,6.18l2.57,7.28c0,2.44-1.98,4.42-4.42,4.42Z"/>
          </svg>
        </div>
        <div class="vehicle-plate ${status}">${plate}</div>
      </div>
    `,
    iconSize: [80, 52],
    iconAnchor: [40, 16]
  });
};

// Cubic ease-out: starts fast, settles into final position
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Shortest-path delta between two compass headings (so a 350°→10° turn goes +20°, not -340°)
const shortestHeadingDelta = (from, to) => {
  let d = to - from;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
};

const ANIMATION_DURATION = 1500;

// Marker that smoothly tweens to new lat/lng + heading between polls instead of jumping.
// The Leaflet marker's position and the inner .vehicle-icon-svg rotation are mutated imperatively
// via a rAF loop; the React Marker's `position` and `icon` props stay stable across animation
// frames so react-leaflet doesn't fight the tween.
// On the phone shell a popup keeps clear of the Vehicles tab (44px) on the
// map's right edge, which sits above Leaflet's popup pane.
const PHONE_POPUP_PAD = [56, 16];

function AnimatedVehicleMarker({ vehicle, onSelect, formatTime, popupPad = null }) {
  const map = useMap();
  const markerRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef({
    lat: vehicle.lat,
    lng: vehicle.lng,
    heading: vehicle.heading || 0,
    status: vehicle.status
  });
  // Latest reported position, so a tween interrupted by a zoom can snap
  // straight to where the vehicle actually is.
  const targetRef = useRef({ lat: vehicle.lat, lng: vehicle.lng, heading: vehicle.heading || 0 });

  // Position prop is captured once on mount; subsequent moves are imperative.
  const [initialPosition] = useState([vehicle.lat, vehicle.lng]);

  // Icon is rebuilt only when the bits that affect its DOM shape change (status colour, plate).
  // Heading is excluded so we don't recreate the divIcon at 60fps during a turn.
  const icon = useMemo(
    () => createVehicleIcon({ ...vehicle, heading: stateRef.current.heading }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicle.status, vehicle.registration, vehicle.name]
  );

  // When the icon DOM is swapped in, push the current animated heading onto the fresh element.
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;
    const el = marker.getElement();
    if (!el) return;
    const iconEl = el.querySelector('.vehicle-icon-svg');
    if (iconEl) iconEl.style.transform = `rotate(${stateRef.current.heading}deg)`;
  }, [icon]);

  // Stop tweening while Leaflet is animating a zoom.
  //
  // Leaflet zooms by applying a CSS transform to the whole marker pane and only
  // reprojects markers at zoomend. Calling setLatLng() from a rAF loop midway
  // through that transform makes Leaflet recompute the marker's layer point
  // against a half-applied projection, so the lorry visibly slides away from
  // the road while the map is still settling — which reads as the position
  // being imprecise. Cancelling the tween and snapping to the real reported
  // position keeps the marker pinned to its true coordinates through the zoom.
  useEffect(() => {
    const onZoomStart = () => {
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
        animRef.current = null;
      }
      const marker = markerRef.current;
      const target = targetRef.current;
      if (marker && target.lat != null && target.lng != null) {
        marker.setLatLng([target.lat, target.lng]);
        const el = marker.getElement();
        if (el) {
          const iconEl = el.querySelector('.vehicle-icon-svg');
          if (iconEl) iconEl.style.transform = `rotate(${target.heading}deg)`;
        }
        stateRef.current = { ...stateRef.current, ...target };
      }
    };

    map.on('zoomstart', onZoomStart);
    return () => { map.off('zoomstart', onZoomStart); };
  }, [map]);

  // Kick off a tween when the target position or heading changes.
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;

    const targetLat = vehicle.lat;
    const targetLng = vehicle.lng;
    const targetHeading = vehicle.heading || 0;
    const current = stateRef.current;

    targetRef.current = { lat: targetLat, lng: targetLng, heading: targetHeading };

    if (
      current.lat === targetLat &&
      current.lng === targetLng &&
      current.heading === targetHeading
    ) {
      return;
    }

    if (animRef.current) cancelAnimationFrame(animRef.current);

    const fromLat = current.lat;
    const fromLng = current.lng;
    const fromHeading = current.heading;
    const headingDelta = shortestHeadingDelta(fromHeading, targetHeading);
    const startTime = performance.now();

    const step = (now) => {
      const t = Math.min((now - startTime) / ANIMATION_DURATION, 1);
      const eased = easeOutCubic(t);
      const lat = fromLat + (targetLat - fromLat) * eased;
      const lng = fromLng + (targetLng - fromLng) * eased;
      const heading = fromHeading + headingDelta * eased;

      marker.setLatLng([lat, lng]);
      const el = marker.getElement();
      if (el) {
        const iconEl = el.querySelector('.vehicle-icon-svg');
        if (iconEl) iconEl.style.transform = `rotate(${heading}deg)`;
      }

      stateRef.current = { ...stateRef.current, lat, lng, heading };

      if (t < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        animRef.current = null;
      }
    };

    animRef.current = requestAnimationFrame(step);
  }, [vehicle.lat, vehicle.lng, vehicle.heading]);

  // Cleanup any in-flight rAF on unmount.
  useEffect(() => () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
  }, []);

  return (
    <Marker
      ref={markerRef}
      position={initialPosition}
      icon={icon}
      eventHandlers={{ click: () => onSelect(vehicle.id) }}
    >
      {/* Options are read when a popup is made, so the key makes a new one if
          the phone shell starts or stops while the map is open. */}
      <Popup key={popupPad ? 'phone' : 'desk'}
             {...(popupPad ? { autoPanPaddingBottomRight: popupPad, autoPanPaddingTopLeft: [16, 16] } : {})}>
        <div className="fleet-popup">
          <div className="fleet-popup-title">
            {vehicle.registration || vehicle.name}
          </div>
          <div className="fleet-popup-row">
            <span>Status</span>
            <span className={`status-${vehicle.status}`}>
              {vehicle.status === 'moving' ? 'Moving' : vehicle.status === 'idle' ? 'Idle' : 'Off'}
            </span>
          </div>
          <div className="fleet-popup-row">
            <span>Speed</span>
            <span className="speed-value">{vehicle.speed ? Math.round(vehicle.speed) + ' mph' : '0 mph'}</span>
          </div>
          {vehicle.fuelLevel !== undefined && (
            <div className="fleet-popup-row">
              <span>Fuel</span>
              <span>{vehicle.fuelLevel}%</span>
            </div>
          )}
          <div className="fleet-popup-row">
            <span>Driver</span>
            <span>{vehicle.driver || '-'}</span>
          </div>
          <div className="fleet-popup-row">
            <span>Updated</span>
            <span>{formatTime(vehicle.lastUpdate)}</span>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

// Tile layer URLs for theme switching.
//
// CARTO now requires an API key on its raster basemaps — unauthenticated tiles
// come back with an "API KEY REQUIRED" watermark rendered into the image. The
// key lives in the server .env and is served by GET /api/config/map, so it can
// be rotated with a --no-build deploy instead of a client rebuild. These
// literals are the unkeyed fallback: if the config fetch fails the map still
// draws, just watermarked.
let TILE_LAYERS = {
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
  },
  light: {
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
  }
};

// Fetched once per page load, not per mount — the component remounts on every
// navigation back to Fleet Tracking and the config never changes in between.
let tileConfigPromise = null;
function loadTileConfig() {
  if (!tileConfigPromise) {
    tileConfigPromise = fetch(`${API_URL}/api/config/map`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.tiles?.dark?.url && data?.tiles?.light?.url) {
          TILE_LAYERS = data.tiles;
        }
        return TILE_LAYERS;
      })
      .catch(() => TILE_LAYERS);
  }
  return tileConfigPromise;
}

// Component to handle tile layer switching based on theme
function TileLayerSwitcher() {
  const map = useMap();
  const tileLayerRef = useRef(null);

  useEffect(() => {
    const updateTileLayer = () => {
      const theme = localStorage.getItem('theme') || 'dark';
      const tileConfig = TILE_LAYERS[theme] || TILE_LAYERS.dark;

      // Remove existing tile layer if any
      if (tileLayerRef.current) {
        map.removeLayer(tileLayerRef.current);
      }

      // Add new tile layer
      tileLayerRef.current = L.tileLayer(tileConfig.url, {
        attribution: tileConfig.attribution,
        subdomains: 'abcd',
        maxZoom: 19
      }).addTo(map);
    };

    // Initial setup — wait for the keyed tile URLs so the first tiles drawn
    // aren't the watermarked unkeyed ones (Leaflet would cache them in the
    // browser for six months).
    let cancelled = false;
    loadTileConfig().then(() => {
      if (!cancelled) updateTileLayer();
    });

    // Listen for theme changes from localStorage
    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        updateTileLayer();
      }
    };

    // Listen for custom theme change events (from Dashboard.js toggle)
    const handleThemeChange = () => {
      updateTileLayer();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('themechange', handleThemeChange);

    // Also check periodically in case theme changes via other means
    const observer = new MutationObserver(() => {
      const currentTheme = localStorage.getItem('theme') || 'dark';
      const expectedUrl = TILE_LAYERS[currentTheme].url;
      if (tileLayerRef.current && tileLayerRef.current._url !== expectedUrl) {
        updateTileLayer();
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => {
      cancelled = true;
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themechange', handleThemeChange);
      observer.disconnect();
      if (tileLayerRef.current) {
        map.removeLayer(tileLayerRef.current);
      }
    };
  }, [map]);

  return null;
}

// Component to handle map view changes and auto-fit bounds
// The first view of the fleet on the phone shell: room for the legend along
// the top and the Vehicles tab on the right, and no more — the desktop's 50px
// all round costs a zoom level on a 340px-wide map, bunching the lorries.
const PHONE_FIT_PADDING = { paddingTopLeft: [16, 48], paddingBottomRight: [56, 16] };

function MapController({ selectedVehicleId, panRequest, vehicles, initialFitDone, setInitialFitDone, phone = false }) {
  const map = useMap();

  // Latest vehicles, readable from the pan effect without making the poll a
  // dependency of it.
  const vehiclesRef = useRef(vehicles);
  vehiclesRef.current = vehicles;

  // Auto-fit bounds on initial load
  useEffect(() => {
    if (!initialFitDone && vehicles.length > 0) {
      const validVehicles = vehicles.filter(v => v.lat && v.lng);
      if (validVehicles.length > 0) {
        const bounds = L.latLngBounds(validVehicles.map(v => [v.lat, v.lng]));
        map.fitBounds(bounds, phone ? { ...PHONE_FIT_PADDING, maxZoom: 12 } : { padding: [50, 50], maxZoom: 12 });
        setInitialFitDone(true);
      }
    }
  }, [vehicles, initialFitDone, setInitialFitDone, map, phone]);

  // Pan to the selected vehicle.
  //
  // Keyed on panRequest — a counter bumped by every click — rather than on the
  // vehicle object. Two bugs came from depending on the object: `vehicles.find`
  // returns a fresh reference on every 30s poll, so the map re-centred itself
  // every half-minute and dragged you back whenever you'd panned elsewhere; and
  // clicking an already-selected vehicle changed nothing, so it never panned
  // back. A counter fires exactly once per click, which is the intent.
  useEffect(() => {
    if (panRequest === 0 || selectedVehicleId == null) return;

    const vehicle = vehiclesRef.current.find(v => v.id === selectedVehicleId);
    if (!vehicle || vehicle.lat == null || vehicle.lng == null) return;

    // Never zoom back out — if you're already closer than 12, stay there.
    map.flyTo([vehicle.lat, vehicle.lng], Math.max(map.getZoom(), 12), { duration: 0.75 });
  }, [panRequest, selectedVehicleId, map]);

  return null;
}

function FleetTracking() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  // Bumped on every selection click so the map pans again even when the same
  // vehicle is re-clicked after you've panned away.
  const [panRequest, setPanRequest] = useState(0);
  const [listExpanded, setListExpanded] = useState(false);
  const [initialFitDone, setInitialFitDone] = useState(false);
  const refreshIntervalRef = useRef(null);
  // The rep phone shell (RepMobile.css lays it out; this decides behaviour).
  const repMobile = useRepMobile();

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/fleet/vehicles`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch vehicles');

      const result = await response.json();

      if (!result.success) throw new Error(result.error || 'Unknown error');

      setVehicles(result.data);
      setLastUpdated(new Date());
      setError(null);
      setLoading(false);

    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
    refreshIntervalRef.current = setInterval(fetchVehicles, 30000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  const movingCount = vehicles.filter(v => v.status === 'moving').length;
  const idleCount = vehicles.filter(v => v.status === 'idle').length;
  const offCount = vehicles.filter(v => v.status === 'off').length;

  // Sort: moving first, then idle, then off, then alphabetically
  const statusOrder = { moving: 0, idle: 1, off: 2 };
  const sortedVehicles = [...vehicles].sort((a, b) => {
    const aOrder = statusOrder[a.status] ?? 2;
    const bOrder = statusOrder[b.status] ?? 2;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (a.registration || a.name || '').localeCompare(b.registration || b.name || '');
  });

  // Always London, never the viewer's zone — the office runs on yard time and
  // an hour-out "last seen" is easy to miss and easy to act on.
  const formatTime = formatTimeLondon;

  if (loading) {
    return (
      <div className="fleet-tracking">
        <div className="fleet-loading">
          <div className="fleet-loading-spinner"></div>
          <span>Loading vehicle data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fleet-tracking">
        <div className="fleet-error">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
          <button onClick={fetchVehicles} className="btn-retry">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fleet-tracking">
      {/* Stats header */}
      <div className="fleet-stats-bar">
        <div className="fleet-stat">
          <span className="fleet-stat-value">{vehicles.length}</span>
          <span className="fleet-stat-label">Total</span>
        </div>
        <div className="fleet-stat">
          <span className="fleet-stat-value moving">{movingCount}</span>
          <span className="fleet-stat-label">Moving</span>
        </div>
        <div className="fleet-stat">
          <span className="fleet-stat-value idle">{idleCount}</span>
          <span className="fleet-stat-label">Idle</span>
        </div>
        <div className="fleet-stat">
          <span className="fleet-stat-value off">{offCount}</span>
          <span className="fleet-stat-label">Off</span>
        </div>
        <div className="fleet-stat last-updated">
          <span className="fleet-stat-value">{lastUpdated ? formatTime(lastUpdated) : '-'}</span>
          <span className="fleet-stat-label">Updated</span>
        </div>
      </div>

      <div className="fleet-content">
        {/* Map - now full width */}
        <div className="fleet-map-container">
          <MapContainer
            center={[50.7, -3.5]}
            zoom={8}
            className="fleet-map"
          >
            <TileLayerSwitcher />
            <MapController
              selectedVehicleId={selectedVehicleId}
              panRequest={panRequest}
              vehicles={vehicles}
              initialFitDone={initialFitDone}
              setInitialFitDone={setInitialFitDone}
              phone={repMobile}
            />

            {vehicles.filter(v => v.lat && v.lng).map(v => (
              <AnimatedVehicleMarker
                key={v.id}
                vehicle={v}
                onSelect={setSelectedVehicleId}
                formatTime={formatTime}
                popupPad={repMobile ? PHONE_POPUP_PAD : null}
              />
            ))}
          </MapContainer>

          <div className="fleet-legend">
            <div className="fleet-legend-item">
              <span className="legend-dot moving"></span>
              <span>Moving</span>
            </div>
            <div className="fleet-legend-item">
              <span className="legend-dot idle"></span>
              <span>Idle</span>
            </div>
            <div className="fleet-legend-item">
              <span className="legend-dot off"></span>
              <span>Ignition Off</span>
            </div>
          </div>

          {/* Expandable vehicle list overlay */}
          <div className={`fleet-list-overlay ${listExpanded ? 'expanded' : ''}`}>
            <button
              className="fleet-list-toggle"
              onClick={() => setListExpanded(!listExpanded)}
            >
              <span className="material-symbols-rounded">
                {listExpanded ? 'chevron_right' : 'chevron_left'}
              </span>
              <span className="toggle-label">Vehicles</span>
            </button>

            <div className="fleet-list-panel">
              <div className="fleet-list-header">Live Vehicles</div>
              <div className="fleet-list-content">
                {sortedVehicles.map(v => {
                  // Some vehicles report no position at all (a tracker with no
                  // GPS fix, or a RAM record with no plate). They're listed —
                  // they're still part of the fleet — but there's nowhere to
                  // pan to, so say so rather than silently ignoring the click.
                  const locatable = v.lat != null && v.lng != null;
                  return (
                  <div
                    key={v.id}
                    className={`fleet-vehicle-card ${v.id === selectedVehicleId ? 'selected' : ''} ${locatable ? '' : 'unlocatable'}`}
                    title={locatable ? undefined : 'No position reported — nothing to show on the map'}
                    onClick={() => {
                      setSelectedVehicleId(v.id);
                      if (locatable) setPanRequest(n => n + 1);
                      // On a phone the open list covers most of the map, and
                      // the vehicle is centred under it — fold it away.
                      if (repMobile && locatable) setListExpanded(false);
                    }}
                  >
                    <div className="fleet-vehicle-header">
                      <span className="fleet-vehicle-name">
                        {v.registration || v.name}
                        <span
                          className={`fleet-provider-tag ${v.provider || 'ram'}`}
                          title={PROVIDER_LABELS[v.provider] || PROVIDER_LABELS.ram}
                        >
                          {v.provider === 'rio' ? 'RIO' : 'RAM'}
                        </span>
                      </span>
                      <span className={`fleet-vehicle-status ${v.status || 'off'}`}>
                        <span className={`status-dot ${v.status || 'off'}`}></span>
                        {v.status === 'moving' ? 'Moving' : v.status === 'idle' ? 'Idle' : 'Off'}
                      </span>
                    </div>
                    <div className="fleet-vehicle-details">
                      <div className="fleet-vehicle-detail">
                        <span>Speed</span>
                        <span className="speed-value">{v.speed ? Math.round(v.speed) + ' mph' : '0 mph'}</span>
                      </div>
                      {v.fuelLevel !== null && v.fuelLevel !== undefined && (
                        <div className="fleet-vehicle-detail">
                          <span>Fuel</span>
                          <span>{v.fuelLevel}%</span>
                        </div>
                      )}
                      {/* RIO reads gross weight off the CAN bus, so we can show
                          the live laden weight. RAM has no equivalent signal. */}
                      {v.weightTotalKg != null && (
                        <div className="fleet-vehicle-detail">
                          <span>Weight</span>
                          <span>{formatTonnes(v.weightTotalKg / 1000)}</span>
                        </div>
                      )}
                      <div className="fleet-vehicle-detail">
                        <span>Driver</span>
                        <span>{v.driver || '-'}</span>
                      </div>
                      <div className="fleet-vehicle-detail">
                        <span>Updated</span>
                        <span>{formatTime(v.lastUpdate)}</span>
                      </div>
                      {!locatable && (
                        <div className="fleet-vehicle-detail fleet-vehicle-nopos">
                          <span>Position</span>
                          <span>Not reported</span>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FleetTracking;
